/**
 * AreaListPanel - Scrollable list of geographic areas with sorting
 */

import React from 'react';
import { Search } from 'lucide-react';
import { onActivateKey } from '../../utils/a11y';
import { sentimentToPercent } from '../../utils/formatters';

const SORT_OPTIONS = [
  { value: 'conversations', label: 'Conversations' },
  { value: 'sentiment', label: 'Avg Sentiment' },
  { value: 'alerts', label: 'Alerts' },
];

/** Sentiment label and color for human-readable display */
const sentimentInfo = (score) => {
  if (score == null) return { text: 'N/A', color: 'text-gray-400', bar: 'bg-gray-300' };
  if (score >= 0.5) return { text: 'Positive', color: 'text-green-600', bar: 'bg-green-500' };
  if (score >= 0.1) return { text: 'Slightly Positive', color: 'text-green-500', bar: 'bg-green-400' };
  if (score >= -0.1) return { text: 'Neutral', color: 'text-gray-500', bar: 'bg-gray-400' };
  if (score >= -0.5) return { text: 'Slightly Negative', color: 'text-amber-600', bar: 'bg-amber-400' };
  return { text: 'Negative', color: 'text-red-600', bar: 'bg-red-500' };
};

/** Classify area zone type for badge coloring */
const getZoneBadge = (area) => {
  if (area.alert_count > 0 || area.complaint_count > area.count * 0.3) {
    return { label: 'Problem', classes: 'bg-red-100 text-red-700' };
  }
  if (area.positive_count > area.count * 0.5) {
    return { label: 'Growth', classes: 'bg-green-100 text-green-700' };
  }
  if (area.count > 0) {
    return { label: 'Demand', classes: 'bg-blue-100 text-blue-700' };
  }
  return null;
};

const AreaListPanel = ({ areas = [], selectedArea, areaSort, onSelectArea, onSortChange }) => {
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    if (!search) return areas;
    const q = search.toLowerCase();
    return areas.filter(a => a.city?.toLowerCase().includes(q));
  }, [areas, search]);

  const sorted = React.useMemo(() => {
    const copy = [...filtered];
    if (areaSort === 'sentiment') {
      copy.sort((a, b) => (a.avg_sentiment_score ?? 0) - (b.avg_sentiment_score ?? 0));
    } else if (areaSort === 'alerts') {
      copy.sort((a, b) => b.alert_count - a.alert_count);
    } else {
      copy.sort((a, b) => b.count - a.count);
    }
    return copy;
  }, [filtered, areaSort]);

  return (
    <div className="card flex flex-col h-full">
      {/* Header with sort */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 flex-shrink-0">Areas ({filtered.length})</h3>
        <div className="relative flex-1 max-w-[140px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-xs border border-gray-300 rounded pl-6 pr-2 py-1 w-full"
          />
        </div>
        <select
          value={areaSort}
          onChange={(e) => onSortChange(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
          aria-label="Sort areas by"
        >
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: 420 }}>
        {sorted.length === 0 && (
          <p className="text-sm text-gray-500 px-4 py-6 text-center">No areas found</p>
        )}
        {sorted.map((area) => {
          const isSelected = selectedArea === area.city;
          const zone = getZoneBadge(area);
          const si = sentimentInfo(area.avg_sentiment_score);

          return (
            <div
              key={area.city}
              role="button"
              tabIndex={0}
              onClick={() => onSelectArea(area.city)}
              onKeyDown={onActivateKey(() => onSelectArea(area.city))}
              className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                isSelected ? 'bg-primary-50 border-l-2 border-l-primary-500' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate">{area.city}</span>
                  {zone && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${zone.classes}`}>
                      {zone.label}
                    </span>
                  )}
                </div>
                <span className="text-xs font-semibold text-gray-700 bg-gray-100 rounded-full px-2 py-0.5 ml-2 flex-shrink-0">
                  {area.count}
                </span>
              </div>

              <div className="flex items-center justify-between mt-1.5">
                {/* Sentiment bar */}
                <div className="flex items-center space-x-1.5">
                  <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${si.bar} rounded-full`}
                      style={{ width: `${sentimentToPercent(area.avg_sentiment_score)}%` }}
                    />
                  </div>
                  <span className={`text-[10px] font-medium ${si.color}`}>
                    {area.avg_sentiment_score != null ? sentimentToPercent(area.avg_sentiment_score) + '%' : 'N/A'}
                  </span>
                </div>

                {area.alert_count > 0 && (
                  <span className="text-[10px] text-red-600 font-medium">
                    {area.alert_count} alert{area.alert_count > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(AreaListPanel);
